import type { TFunction } from 'react-i18next';
import * as yup from 'yup';

import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';

import { isLegitimateInterestsBasis } from './processingActivityEditor.parsers';
import type { ProcessingActivityEditorFormValues, ProcessingActivityEditorPurpose } from './processingActivityEditor.types';

export type ProcessingActivityEditorValidationErrors = Partial<Record<string, string>>;

export type ProcessingActivityEditorValidationMessages = {
  required: string;
  categoryRequired: string;
  purposeRequired: string;
  purposeIncomplete: string;
  legalBasisCommentRequired: string;
};

export const buildDraftValidationSchema = () =>
  yup.object({
    name: yup.string().trim().max(255),
    description: yup.string(),
    processingRole: yup.string().oneOf(['controller', 'processor', '']),
    minimumSecurityMeasures: yup.string(),
    dataSubjectCategories: yup.array(),
    personalDataCategories: yup.array(),
    recipientCategories: yup.array(),
    purposes: yup.array(),
  });

const isPurposeCompleteForPublish = (purpose: ProcessingActivityEditorPurpose): boolean => {
  if (purpose.isSoftDeleted) return true;
  if (!purpose.purposeText?.trim() || !purpose.legalBasis) return false;
  if (!purpose.retentionTrigger?.trim() || purpose.retentionValue == null || purpose.retentionValue < 1 || !purpose.retentionUnit) {
    return false;
  }
  if (isLegitimateInterestsBasis(purpose.legalBasis) && !purpose.legalBasisComment?.trim()) {
    return false;
  }

  return true;
};

export const buildProcessingActivityEditorValidationMessages = (t: TFunction): ProcessingActivityEditorValidationMessages => ({
  required: t(validation_messages.required),
  categoryRequired: t(processing_activities_messages.editor.validation.category_required),
  purposeRequired: t(processing_activities_messages.editor.validation.purpose_required),
  purposeIncomplete: t(processing_activities_messages.editor.validation.purpose_incomplete),
  legalBasisCommentRequired: t(processing_activities_messages.editor.validation.legal_basis_comment_required),
});

export const validateProcessingActivityEditorForPublish = (
  values: ProcessingActivityEditorFormValues,
  messages: ProcessingActivityEditorValidationMessages,
): ProcessingActivityEditorValidationErrors => {
  const errors: ProcessingActivityEditorValidationErrors = {};

  if (!values.name.trim()) {
    errors.name = messages.required;
  }

  if (!values.description.trim()) {
    errors.description = messages.required;
  }

  if (!values.processingRole) {
    errors.processingRole = messages.required;
  }

  if (!values.minimumSecurityMeasures.trim()) {
    errors.minimumSecurityMeasures = messages.required;
  }

  if (values.dataSubjectCategories.length === 0) {
    errors.dataSubjectCategories = messages.categoryRequired;
  }

  if (values.personalDataCategories.length === 0) {
    errors.personalDataCategories = messages.categoryRequired;
  }

  if (values.recipientCategories.length === 0) {
    errors.recipientCategories = messages.categoryRequired;
  }

  const activePurposes = values.purposes.filter(purpose => !purpose.isSoftDeleted);

  if (activePurposes.length === 0) {
    errors.purposes = messages.purposeRequired;
  } else {
    const incompletePurpose = activePurposes.find(purpose => !isPurposeCompleteForPublish(purpose));

    if (incompletePurpose) {
      errors.purposes =
        isLegitimateInterestsBasis(incompletePurpose.legalBasis) && !incompletePurpose.legalBasisComment?.trim()
          ? messages.legalBasisCommentRequired
          : messages.purposeIncomplete;
    }
  }

  return errors;
};

export const hasValidationErrors = (errors: ProcessingActivityEditorValidationErrors) => Object.keys(errors).length > 0;

export const buildPublishDisabledTooltip = (errors: ProcessingActivityEditorValidationErrors): string =>
  Array.from(new Set(Object.values(errors).filter((message): message is string => Boolean(message)))).join('\n');
